/**
 * Analytics event tracking helpers.
 * Ready for GA4, GTM, or any analytics provider.
 * Currently logs to console in dev; replace with real tracking calls.
 */

import { getMeasurementId, gtag } from '@/seo/gtag';
type EventName =
  | 'booking_click'
  | 'booking_cta_click'
  | 'coaching_form_submit'
  | 'speaking_form_submit'
  | 'contact_form_submit'
  | 'form_submit'
  | 'whatsapp_click'
  | 'language_switch'
  | 'blog_cta_click'
  | 'blog_article_view'
  | 'blog_category_filter'
  | 'blog_search'
  | 'blog_language_filter'
  | 'newsletter_signup'
  | 'page_view'
  | 'cta_click'
  | 'media_cta_click'
  | 'locale_switch'
  | 'internal_link_click'
  | 'gpt_launch'
  | 'sign_up'
  | 'generate_lead'
  | 'session_application_submit';

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

export function trackEvent(name: EventName, params?: EventParams) {
  // GA4 via gtag (dataLayer is fed by src/seo/gtag.ts)
  if (getMeasurementId()) {
    gtag('event', name, params ?? {});
  }

  // Dev logging
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${name}`, params);
  }
}

/** Conversion events (registro, CTA y solicitud de sesión de cortesía). */
export const trackSignUp = (method = 'email') =>
  trackEvent('sign_up', { method });

export const trackCtaClick = (label: string, source: string) =>
  trackEvent('cta_click', { cta_label: label, source });

export const trackSessionApplication = (stage: 'start' | 'success' | 'error', params?: EventParams) =>
  trackEvent('session_application_submit', { stage, ...params });

export const trackLead = (source: string, params?: EventParams) =>
  trackEvent('generate_lead', { source, ...params });

// Convenience helpers
export const trackBookingClick = (source: string) =>
  trackEvent('booking_cta_click', { source });

export const trackFormSubmit = (formName: string) =>
  trackEvent('form_submit', { form_name: formName });

export const trackWhatsAppClick = (source: string) =>
  trackEvent('whatsapp_click', { source });

export const trackLanguageSwitch = (from: string, to: string) =>
  trackEvent('locale_switch', { from_locale: from, to_locale: to });

export const trackBlogArticleView = (slug: string, category: string) =>
  trackEvent('blog_article_view', { slug, category });

export const trackBlogCtaClick = (slug: string, ctaType: string) =>
  trackEvent('blog_cta_click', { slug, cta_type: ctaType });

export const trackInternalLink = (from: string, to: string) =>
  trackEvent('internal_link_click', { from_page: from, to_page: to });
